import _ from "lodash";
import React from "react";
import classNames from "classnames";
import classes from "./ChatContainer.scss";
import TopHeader from "./components/TopHeader";
import PropTypes from "prop-types";
import MessagesContainer from "./components/MessagesContainer";
import MessageAttachmentCard from "./components/MessageAttachmentCard";
import { graphql, compose } from "lib/apolloClient";
import { getConversationsQuery } from "AppComponents/ChatContainer/modules/ChatContainerQuery";
import { getConversationsFromCache } from "AppComponents/ChatContainer/modules/ChatContainerHelpers";
import { connect } from "react-redux";
import { updateNavigationObject } from "modules/NavigationModule";
import { ChatIllustration } from "@toddle-edu/ds-theme";
import {
  createMessage,
  deleteMessage,
  updateMessage,
  updateChatInfo,
  markConversationAsRead,
  updateTextboxFocus,
  getChatQueryVariables,
  INTIAL_RECORDS_NUMBER,
} from "AppComponents/ChatContainer/modules/ChatContainerModule";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import withLoader from "UIComponents/GqlWithLoader";
import I18nHOC from "UIComponents/I18nHOC";
import NoDataComponent from "UIComponents/NoDataComponent";
import ButtonDropdown from "UIComponents/ButtonDropdown";
import ProfileCascade from "UIComponents/ProfileCascade";
import DialogueBox from "UIComponents/DialogueBox";
import { generateRandomId, scrollIntoView } from "Utils";
import { getUserArchivalStatus } from "modules/CommonSelectors";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getCloudUrl, colors } from "Constants";

import { Button, IconButton, EmptyState } from "@toddle-edu/ds-web";
import { PinFilled, CloseOutlined } from "@toddle-edu/ds-icons";
import moment from "moment";
import { ATTACHMENT_OMIT_ATTRIBUTES } from "store/static";
import ChatbarWrapper from "UIComponents/ChatbarWrapper/ChatbarWrapper";
import { checkScrollAtTop } from "services/checkScrollAtTop";

const itemStyle = {
  labelStyle: {
    fontSize: "1.4rem",
  },
};

const DIALOGS_INFO = {
  UNPIN_MESSAGE: {
    title: "classRoom:un_pin_remark",
    message: "classRoom:un_pin_message_alert",
    confirmButtonText: "classRoom:un_pin",
    cancelButtonText: "common:cancel",
  },
  DELETE_MESSAGE: {
    title: "common:delete_with_label",
    label: "common:message",
    message: "classRoom:delete_message_alert",
    confirmButtonText: "common:delete",
    cancelButtonText: "common:cancel",
  },
};

const getMessageDataStoreKey = ({ parentType, parentId }) => {
  return `${parentType}_${parentId}`;
};

class ChatContainerLocal extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      mode: "VIEW",
      messageDetails: {
        label: "",
        isEditMode: false,
        attachments: [],
        id: generateRandomId(),
      },
      isPinMessageLoading: false,
      currentDialogAction: "",
      currentDialogData: {},
    };
    this.messagesRefs = {};
    this.scrollTop = 0;
    this.currentSection = null;
  }

  componentDidMount = () => {
    const {
      parentType,
      conversationId,
      parentId,
      updateChatInfo,
      markConversationAsRead,
      excludeItemTypesV2,
      includeItemTypesV2,
      unreadMessageItemTypes,
      shouldMarkConversationAsRead,
      refetchQueries = [],
    } = this.props;
    if (conversationId) {
      if (shouldMarkConversationAsRead) {
        markConversationAsRead({
          id: conversationId,
          excludeItemTypesV2,
          includeItemTypesV2,
          unreadMessageItemTypes,
          refetchQueries,
        });
      }
      updateChatInfo({
        parentId,
        parentType,
        conversationId,
        shouldMarkConversationAsRead,
      });
    }
    this.setScrollToTop();
  };

  componentWillUnmount = () => {
    this.props.updateChatInfo({
      parentId: null,
      parentType: "",
      conversationId: null,
      shouldMarkConversationAsRead: true,
    });
  };

  componentDidUpdate = prevProps => {
    const {
      parentType,
      conversationId,
      parentId,
      updateChatInfo,
      markConversationAsRead,
      excludeItemTypesV2,
      shouldMarkConversationAsRead,
      isScrollAtBottom,
      unreadCount,
      includeItemTypesV2,
      unreadMessageItemTypes,
      refetchQueries = [],
    } = this.props;
    if (
      !_.isNil(this.props.chatBox) &&
      this.props.chatBox != prevProps.chatBox
    ) {
      this.doNotificationTask();
    }

    if (conversationId && prevProps.conversationId != conversationId) {
      this.setScrollToTop();
      updateChatInfo({
        parentId,
        parentType,
        conversationId,
        shouldMarkConversationAsRead,
      });

      if (shouldMarkConversationAsRead) {
        markConversationAsRead({
          id: conversationId,
          excludeItemTypesV2,
          includeItemTypesV2,
          unreadMessageItemTypes,
          refetchQueries,
        });
      }
    }

    if (
      prevProps.isScrollAtBottom !== isScrollAtBottom &&
      isScrollAtBottom &&
      unreadCount > 0
    ) {
      this.onClickNewMessageChip();
    }

    if (prevProps.parentId !== parentId) {
      this.changeToViewMode();
    }
  };

  getMoreResults = async scrollContainer => {
    const {
      fetchMoreFeed,
      pageInfo,
      networkStatus,
      totalCounts,
      conversations,
    } = this.props;

    this.scrollTop = scrollContainer.scrollTop;
    if (
      scrollContainer.scrollTop === 0 &&
      pageInfo.hasNextPage &&
      networkStatus !== 3
    ) {
      fetchMoreFeed().then(() => {
        const scrollIndex =
          totalCounts == conversations.length
            ? (totalCounts % INTIAL_RECORDS_NUMBER) - 1
            : INTIAL_RECORDS_NUMBER - 1;

        if (scrollIndex >= 0 && this.messagesRefs[scrollIndex]) {
          scrollContainer.scrollTop =
            this.messagesRefs[scrollIndex].getBoundingClientRect().top -
            scrollContainer.getBoundingClientRect().top;
        }
      });
    }

    let currentSection = null;
    if (scrollContainer) {
      _.map(this.messagesRefs, (ref, key) => {
        if (ref) {
          const { top } = ref.getBoundingClientRect();
          if (top < scrollContainer.clientHeight / 3 && top >= 0) {
            currentSection = key;
          }
        }
      });
    }

    if (this.currentSection != currentSection && !!currentSection) {
      this.currentSection = currentSection;
    }
  };

  handleLoadPreviousMessages = async () => {
    const { fetchMoreFeed, pageInfo, networkStatus } = this.props;

    const loadPreviousMessages =
      this.scrollContainer &&
      checkScrollAtTop(this.scrollContainer) &&
      pageInfo?.hasNextPage &&
      networkStatus !== 3;

    if (loadPreviousMessages) {
      const previousScrollHeight = this.scrollContainer?.scrollHeight;
      await fetchMoreFeed();
      setTimeout(() => {
        if (this.scrollContainer) {
          this.scrollContainer.scrollTop =
            this.scrollContainer.scrollHeight - previousScrollHeight;
        }
      });
    }
  };

  handleUpdateMessage = async conversation => {
    const {
      collaborators,
      setToastMsg,
      updateMessage,
      onMessageSend,
      isV2,
    } = this.props;

    const { label, attachments, id, type = "", isPin = false } = conversation;

    const response = await updateMessage({
      id,
      label,
      type,
      isPin,
      collaborators,
      attachments: _.map(attachments, attachment =>
        _.omit(attachment, ["id", ...ATTACHMENT_OMIT_ATTRIBUTES])
      ),
    });

    if (!isV2) {
      this.changeToViewMode();
    }

    if (response && !response.successful) {
      setToastMsg(response.errors[0]);
      return;
    }

    if (onMessageSend) {
      onMessageSend();
    }
  };

  handlePinMessage = async conversation => {
    const {
      collaborators,
      setToastMsg,
      updateMessage,
      conversationId,
      showAllMessages,
      showInstructionMessage,
      excludedItemTypesForConversation,
      includeDeletedMessage,
      onMessageSend,
      isV2,
    } = this.props;

    const { label, attachments, id, type = "", isPin = false } = conversation;

    this.setState({ isPinMessageLoading: true });
    const response = await updateMessage({
      id,
      label,
      type,
      isPin,
      collaborators,
      attachments: _.map(attachments, attachment =>
        _.omit(attachment, ["id", ...ATTACHMENT_OMIT_ATTRIBUTES])
      ),
      conversationId,
      showAllMessages,
      showInstructionMessage,
      extraFilters: {
        excludedItemTypesV2: excludedItemTypesForConversation,
        includeDeletedMessage,
      },
      isPinToggle: true,
    });
    this.setState({ isPinMessageLoading: false });

    if (!isV2) {
      this.changeToViewMode();
    }

    if (response && !response.successful) {
      setToastMsg(response.errors[0]);
      return;
    }

    if (onMessageSend) {
      onMessageSend();
    }
  };

  handleCreateMessage = async conversation => {
    const {
      collaborators,
      setToastMsg,
      conversationId,
      showAllMessages,
      showInstructionMessage,
      excludedItemTypesForConversation,
      includeDeletedMessage,
      shouldSendCurriculumIdInMessage,
      curriculumProgramId,
      createMessage,
      onMessageSend,
      isV2,
    } = this.props;

    const { label, attachments, type = "" } = conversation;

    const messagePayLoad = {
      id: conversationId,
      showAllMessages,
      showInstructionMessage,
      collaborators,
      label,
      attachments: _.map(attachments, attachment =>
        _.omit(attachment, ["id", ...ATTACHMENT_OMIT_ATTRIBUTES])
      ),
      extraFilters: {
        excludedItemTypesV2: excludedItemTypesForConversation,
        includeDeletedMessage,
      },
      curriculumProgramId: shouldSendCurriculumIdInMessage
        ? curriculumProgramId
        : undefined,
    };
    if (!_.isEmpty(type)) {
      messagePayLoad.type = type;
    }
    const response = await createMessage(messagePayLoad);

    if (!isV2) {
      this.changeToViewMode();
    }

    if (response && !response.successful) {
      setToastMsg(response.errors[0]);
      return;
    }

    setTimeout(() => {
      this.setScrollToTop({ shouldCallParentScrollTop: true });
    }, 400);

    if (onMessageSend) {
      onMessageSend();
    }
  };

  handleEmptyMessageToast = () => {
    const { setToastMsg } = this.props;

    setToastMsg({
      msg: "toastMsgs:label_cannot_be_emty",
      locale_params: [
        { key: "label", value: "common:message", isPlainText: false },
      ],
      type: "warning",
    });
  };

  sendMessage = conversation => {
    const { isV2 } = this.props;
    const { label, attachments } = conversation;
    const currentMode = this.state.mode;

    if ((label && !_.isEmpty(_.trim(label))) || !_.isEmpty(attachments)) {
      switch (currentMode) {
        case "EDIT":
          this.handleUpdateMessage(conversation);
          break;
        case "PIN_MESSAGE":
          this.handlePinMessage(conversation);
          break;
        default:
          this.handleCreateMessage(conversation);
      }
    } else {
      this.handleEmptyMessageToast();
    }

    if (isV2) {
      this.changeToViewMode();
    }
  };

  deleteMessageFunc = async messageDetails => {
    const {
      deleteMessage,
      conversationId,
      setToastMsg,
      showAllMessages,
      showInstructionMessage,
      excludedItemTypesForConversation,
      includeDeletedMessage,
    } = this.props;

    const { successful, errors } = await deleteMessage({
      id: messageDetails,
      conversationId,
      showAllMessages,
      showInstructionMessage,
      extraFilters: {
        excludedItemTypesV2: excludedItemTypesForConversation,
        includeDeletedMessage,
      },
    });

    if (!successful) {
      setToastMsg(errors[0]);
    }

    this.changeToViewMode();
  };

  deleteMessage = async messageDetails => {
    this.showDialogueConfirmation({
      action: "DELETE_MESSAGE",
      data: messageDetails,
    });
  };

  deleteMessageV2 = ({ messageId }) => {
    this.deleteMessageFunc(messageId);
  };

  editMessage = messageDetails => {
    const { chatPanelRef } = this.props;
    this.setState({
      mode: "EDIT",
      messageDetails: {
        label: _.get(messageDetails, "label", ""),
        attachments: _.get(messageDetails, "item.attachments", []),
        id: messageDetails.id,
        isEditMode: true,
        type: _.get(messageDetails, "type", ""),
        isPin: _.get(messageDetails, "isPin", false),
      },
    });
    if (_.isFunction(chatPanelRef?.current?.setChatbarContent)) {
      chatPanelRef.current.setChatbarContent(
        _.get(messageDetails, "label", "")
      );
    }
  };

  editMessageV2 = ({ message }) => {
    this.editMessage(message);
  };

  togglePinMessageFunc = messageDetails => {
    const messageType = _.get(messageDetails, "type", "");
    this.sendMessage({
      label: _.get(messageDetails, "label", ""),
      attachments: _.get(messageDetails, "item.attachments", []),
      id: _.get(messageDetails, "id", ""),
      type: _.isEqual(messageType, "REMARK") ? "NORMAL" : "REMARK",
      isPin: _.isEqual(messageType, "REMARK") ? false : true,
    });
  };

  togglePinMessage = messageDetails => {
    this.setState(
      {
        mode: "PIN_MESSAGE",
      },
      this.togglePinMessageFunc.bind(this, messageDetails)
    );
  };

  changeToViewMode = () => {
    const { chatPanelRef } = this.props;
    this.setState({
      mode: "VIEW",
      messageDetails: {
        label: "",
        attachments: [],
        isEditMode: false,
        id: generateRandomId(),
      },
    });
    if (_.isFunction(chatPanelRef?.current?.setChatbarContent)) {
      chatPanelRef.current.setChatbarContent("");
    }
  };

  doNotificationTask = () => {
    this.setScrollToTop();
  };

  setScrollToTop = ({ shouldCallParentScrollTop = false } = {}) => {
    const { setScrollToTop } = this.props;
    if (this.scrollContainer) {
      this.scrollContainer.scrollTop =
        this.scrollContainer.clientHeight + this.scrollContainer.scrollHeight;
    }
    if (setScrollToTop && shouldCallParentScrollTop) {
      setScrollToTop();
    }
  };

  renderNoDataComp = (userType, conversations = [], t, noDataCompStyle) => {
    const { isCurrentAcademicYear } = this.props;
    if (userType === "parent" && conversations.length === 1) {
      return (
        <NoDataComponent
          emptyText={
            !isCurrentAcademicYear
              ? t("common:no_discussion_messages_for_previous_year")
              : t("common:no_discussion_messages")
          }
          isSearch={false}
          emptyHeaderText={""}
          emptyIcon={
            <img src={ChatIllustration} width={272} height={192} alt="" />
          }
          containerStyle={noDataCompStyle}
        />
      );
    }
  };

  isMsgVisibile = (userType, totalMessageCount) =>
    userType !== "parent" ? true : totalMessageCount === 0 ? false : true;

  showDialogueConfirmation = ({ action, data }) => {
    this.setState({ currentDialogAction: action, currentDialogData: data });
  };

  onToggleDialogueClick = () => {
    this.setState({ currentDialogAction: "", currentDialogData: {} });
  };

  dialogueConfirmClick = () => {
    const { currentDialogAction, currentDialogData } = this.state;

    switch (currentDialogAction) {
      case "UNPIN_MESSAGE":
        this.togglePinMessage(currentDialogData);
        break;
      case "DELETE_MESSAGE":
        this.deleteMessageFunc(currentDialogData);
        break;
    }
  };

  renderViewPinnedRemark = ({ pinMessages }) => {
    const {
      t,
      studentData,
      senderNameStyle,
      messageWrapperStyle,
      showRemarkOption,
      userType,
      messageSubConStyle,
      attachmentListContainerStyle,
    } = this.props;
    const viewPinnedRemarkText = t("classRoom:view_pinned_remark");
    const profileImage = _.get(
      pinMessages,
      "edges[0].node.createdBy.profileImage",
      ""
    );
    const firstName = _.get(
      pinMessages,
      "edges[0].node.createdBy.firstName",
      ""
    );
    const lastName = _.get(pinMessages, "edges[0].node.createdBy.lastName", "");
    const createdAt = _.get(pinMessages, "edges[0].node.createdAt", "");
    const containerStyle = {
      zIndex: 20,
      borderRadius: 8,
    };
    const horizontalCardStyle = {
      backgroundColor: "#f8f8f8",
      padding: "16px",
      borderRadius: "8px",
    };
    const buttonComponent = (
      <Button
        size={"small"}
        variant={"plain"}
        icon={<PinFilled size={"xxx-small"} variant={"default"} />}
      >
        {viewPinnedRemarkText}
      </Button>
    );
    const dropdownComponent = (
      <div className={classes.dropdownComponent}>
        <div className={classes.pinMessageHeader}>
          <div className={classes.profile}>
            <ProfileCascade
              customStyle={{
                width: 24,
                height: 24,
              }}
              items={[
                {
                  imageUrl: profileImage,
                  value: firstName,
                },
              ]}
            />
            <div
              className={classes.nameLabel}
            >{`${firstName} ${lastName}`}</div>
          </div>
          <PinFilled
            size={"xxx-small"}
            variant={"link"}
            onClick={() =>
              this.showDialogueConfirmation({
                action: "UNPIN_MESSAGE",
                data: _.get(pinMessages, "edges[0].node", {}),
              })
            }
          />
        </div>
        <div className={classes.pinMessage}>
          <MessageAttachmentCard
            isRemark={true}
            isOwnChat={true}
            message={_.get(pinMessages, "edges[0].node.label", "")}
            itemType={_.get(pinMessages, "edges[0].node.itemType", "")}
            item={_.get(pinMessages, "edges[0].node.item", {})}
            createdBy={_.get(pinMessages, "edges[0].node.createdBy", {})}
            itemStyle={itemStyle}
            showSenderInfo={false}
            showMessageTime={false}
            showFlatMessage={true}
            studentData={studentData}
            attachmentCardType={"horizontalV2"}
            horizontalCardStyle={horizontalCardStyle}
            isRightPaneChat={true}
            senderNameStyle={senderNameStyle}
            messageWrapperStyle={messageWrapperStyle}
            showRemarkOption={showRemarkOption}
            pinMessages={pinMessages}
            userType={userType}
            isPinPopup={true}
            messageSubConStyle={messageSubConStyle}
            attachmentListContainerStyle={attachmentListContainerStyle}
          />
        </div>
        <div className={classes.date}>
          {moment(createdAt).format("D MMM YYYY")}
        </div>
      </div>
    );

    return (
      <ButtonDropdown
        buttonComponent={buttonComponent}
        dropdownComponent={dropdownComponent}
        containerStyle={containerStyle}
      />
    );
  };

  onClickNewMessageChip = async () => {
    const {
      conversationId,
      scrollEndRef,
      excludeItemTypesV2,
      markConversationAsRead,
      getConversations,
      parentType,
    } = this.props;

    if (scrollEndRef) {
      scrollIntoView({
        element: scrollEndRef.current,
        block: "end",
        inline: "end",
      });
    }

    markConversationAsRead({
      id: conversationId,
      excludeItemTypesV2,
      shouldRefetchClassRoomQuery: _.includes(
        ["ASSIGNMENT", "STUDENT_ASSIGNMENT"],
        parentType
      ),
    });

    await getConversations.refetch();
  };

  renderPrivateMessagesCount = ({
    totalMessageCount,
    labelKeySingular,
    labelKeyPlural,
  }) => {
    const { t, hideZeroPrivateMessageCount } = this.props;
    if (hideZeroPrivateMessageCount && totalMessageCount === 0) {
      return t(labelKeySingular);
    } else {
      return t(labelKeyPlural, {
        count: totalMessageCount,
      });
    }
  };

  render() {
    const {
      isV2,
      conversations,
      networkStatus,
      conversationId,
      userId,
      userDetails,
      showUserDetails,
      showChatHeader,
      chatHeaderTitle,
      hideCloseButton,
      onClose,
      showSenderInfoAlways,
      conversationLabel,
      deleteOtherMessage,
      footerContainerStyle,
      showFlatMessage,
      showMessageCount,
      totalMessageCount,
      inputTextPlaceHolder,
      studentData,
      flatMessageConStyle,
      footerBottomConStyle,
      footerTextBoxStyle,
      footerSendBtnStyle,
      isTeacherEvaluationEnabled,
      isStudentEvaluationEnabled,
      messagesConStyle,
      noDataCompStyle,
      messagesScrollStyle,
      otherUserId,
      allowSendMessage,

      attachmentCardType,
      horizontalCardStyle,
      isRightPaneChat,
      mode, // disabling message dropdown in view mode
      t,
      isClassDiscussionEnabled,
      showNoDataComp,
      userType,
      showRemarkOption,
      addCommentFooter,
      pinMessages,
      profileIconStyle,
      senderNameStyle,
      messageWrapperStyle,
      addAttachmentBtnStyle,
      textAreaCustomStyles,
      isTaggingEnabled,
      collaborators,
      parentType,
      taggableTextAreaProps,
      updateTextboxFocus,
      emptyViewStyle,
      emptyHeaderText,
      emptyText,
      showEmptyViewDescription,
      privateMessageLabelStyle,
      unreadCount,
      customMessageContainerClass,
      messageItemStyle,
      footerTextAreaStyle,
      messageSubConStyle,
      attachmentListContainerStyle,
      flatMsgContainerStyle,
      emptyStateProps,
      labelKeySingular,
      labelKeyPlural,
      messageContextLabel,
      showCommentContextBox,
      messageContextBoxStyle,
      horizontalMessageCardV2,
      dropDownConStyle,
      attachmentListContainerStyleV2,
      scrollLatestMessage,
      emptyComponent,
      disabledChatComponent = null,
      isEditAndDeleteEnabled,
      testIdPrefix,
      showLightWeightAvatar,
      saveDraftMessage,
      parentId,
      isAudioRecordingEnabled,
      dsVersion,
      chatPanelRef,
      enableChatPanel,
      allowDeleteForAllMessages,
      messageContainerStyle,
      screenMode,
      isMediaRestricted = false,
    } = this.props;
    const { isPinMessageLoading, currentDialogAction } = this.state;
    const viewPinRemarkEnable = _.size(_.get(pinMessages, "edges", [])) > 0;

    const studentArchived = _.get(studentData, "isArchived", false);

    const chatBarContainerClasses = classNames({
      [classes.chatContainerDiv]: true,
      [classes.chatBarContainerWithExtraPadding]: isV2,
    });

    const isStudentArchived = _.has(studentData, "isArchivedV2")
      ? getUserArchivalStatus(studentData)
      : studentArchived;

    const isArchived =
      _.get(userDetails, "isArchived", false) || isStudentArchived;

    const canSendMessage = !isArchived && allowSendMessage;

    const filteredOutSystemConversation = _.filter(
      conversations,
      ({ node }) => {
        return _.get(node, "type") !== "SYSTEM_GENERATED_GROUP_CHAT_SECOND";
      }
    );

    return (
      <div className={classes.container}>
        {showNoDataComp &&
          this.renderNoDataComp(userType, conversations, t, noDataCompStyle)}
        {showMessageCount &&
          this.isMsgVisibile(userType, totalMessageCount) &&
          (showFlatMessage && showRemarkOption && viewPinRemarkEnable ? (
            <div
              className={classes.privateMessageLabel}
              style={privateMessageLabelStyle}
            >
              <div className={classes.viewPinnedRemark}>
                {this.renderViewPinnedRemark({
                  pinMessages,
                })}
              </div>
            </div>
          ) : null)}
        {showUserDetails ? (
          <TopHeader
            userDetails={userDetails}
            showRemarkOption={showRemarkOption}
            viewPinRemarkEnable={viewPinRemarkEnable}
            renderViewPinnedRemark={this.renderViewPinnedRemark}
            pinMessages={pinMessages}
          />
        ) : null}
        {showChatHeader ? (
          <div className={classes.headerV2}>
            <div className={classes.headerDetailsV2}>
              <div className={classes.headingV2}>
                {chatHeaderTitle || t("common:chat")}
              </div>
            </div>
            {!hideCloseButton && (
              <div className={classes.closeIcon}>
                <IconButton
                  icon={<CloseOutlined size={"xx-small"} variant={"subtle"} />}
                  size={"medium"}
                  variant={"plain"}
                  onClick={onClose}
                  aria-label={t("common:close")}
                />
              </div>
            )}
          </div>
        ) : null}
        {_.isEmpty(
          isClassDiscussionEnabled && userType === "student"
            ? filteredOutSystemConversation
            : conversations
        ) ? (
          emptyComponent ? (
            emptyComponent
          ) : (
            <React.Fragment>
              {showCommentContextBox && (
                <div
                  className={classes.commentContextBox}
                  style={messageContextBoxStyle}
                >
                  <div className={classes.commentLabel}>
                    {messageContextLabel}
                  </div>
                </div>
              )}
              <div
                className={classes.emptyStateContainer}
                style={emptyViewStyle}
              >
                <EmptyState
                  illustration={ChatIllustration}
                  title={t(emptyHeaderText)}
                  subtitle={showEmptyViewDescription ? t(emptyText) : ""}
                  size="small"
                  {...emptyStateProps}
                />
              </div>
            </React.Fragment>
          )
        ) : (
          <MessagesContainer
            isV2={isV2}
            userId={userId}
            senderNameStyle={senderNameStyle}
            showSenderInfoAlways={showSenderInfoAlways}
            conversationId={conversationId}
            conversations={conversations}
            networkStatus={networkStatus}
            containerScroll={this.getMoreResults}
            deleteMessage={this.deleteMessage}
            editMessage={this.editMessage}
            customItemRef={(ref, index, id) => {
              this.messagesRefs[index] = ref;
            }}
            setScrollContainerRef={ref => (this.scrollContainer = ref)}
            deleteOtherMessage={deleteOtherMessage}
            showFlatMessage={showFlatMessage}
            studentData={studentData}
            isTeacherEvaluationEnabled={isTeacherEvaluationEnabled}
            isStudentEvaluationEnabled={isStudentEvaluationEnabled}
            messagesConStyle={messagesConStyle}
            flatMessageConStyle={flatMessageConStyle}
            allowSendMessage={canSendMessage}
            attachmentCardType={attachmentCardType}
            horizontalCardStyle={horizontalCardStyle}
            isRightPaneChat={isRightPaneChat}
            mode={mode}
            isClassDiscussionEnabled={isClassDiscussionEnabled}
            parentType={parentType}
            messagesScrollStyle={messagesScrollStyle}
            otherUserId={otherUserId}
            showRemarkOption={showRemarkOption}
            togglePinMessage={this.togglePinMessage}
            isPinMessageLoading={isPinMessageLoading}
            pinMessages={pinMessages}
            messageWrapperStyle={messageWrapperStyle}
            profileIconStyle={profileIconStyle}
            customMessageContainerClass={customMessageContainerClass}
            messageItemStyle={messageItemStyle}
            messageSubConStyle={messageSubConStyle}
            attachmentListContainerStyle={attachmentListContainerStyle}
            flatMsgContainerStyle={flatMsgContainerStyle}
            showCommentContextBox={showCommentContextBox}
            messageContextLabel={messageContextLabel}
            messageContextBoxStyle={messageContextBoxStyle}
            horizontalMessageCardV2={horizontalMessageCardV2}
            dropDownConStyle={dropDownConStyle}
            attachmentListContainerStyleV2={attachmentListContainerStyleV2}
            scrollLatestMessage={scrollLatestMessage}
            isEditAndDeleteEnabled={isEditAndDeleteEnabled}
            showAvatar={showLightWeightAvatar}
            deleteMessageV2={this.deleteMessageV2}
            editMessageV2={this.editMessageV2}
            handleLoadPreviousMessages={this.handleLoadPreviousMessages}
            allowDeleteForAllMessages={allowDeleteForAllMessages}
            messageContainerStyle={messageContainerStyle}
            screenMode={screenMode}
            renderLinksAsAnchor={!isMediaRestricted}
          />
        )}
        {canSendMessage ? (
          <div className={chatBarContainerClasses} key={conversationId}>
            <ChatbarWrapper
              messageData={this.state.messageDetails}
              sendMessage={this.sendMessage}
              footerMsgContainerV2={showRemarkOption}
              isTaggingEnabled={isTaggingEnabled}
              collaborators={collaborators}
              parentType={parentType}
              mode={this.state.mode}
              inputTextPlaceHolder={inputTextPlaceHolder}
              cancelEditMessage={this.changeToViewMode}
              isAudioRecordingEnabled={isAudioRecordingEnabled}
              testIdPrefix={testIdPrefix}
              draftMessageStoreKey={getMessageDataStoreKey({
                parentId,
                parentType,
              })}
              saveDraftMessage={saveDraftMessage}
              chatPanelRef={chatPanelRef}
              enableChatPanel={enableChatPanel}
              showAddButton={!isMediaRestricted}
            />
          </div>
        ) : (
          disabledChatComponent
        )}
        {currentDialogAction && (
          <DialogueBox
            modalTitle={t(DIALOGS_INFO[currentDialogAction].title, {
              label: t(DIALOGS_INFO[currentDialogAction].label),
            })}
            showModal={true}
            toggleDialogueBoxDisplay={this.onToggleDialogueClick}
            onClickButton2={this.dialogueConfirmClick}
            modalBody={t(DIALOGS_INFO[currentDialogAction].message)}
            button1={t(DIALOGS_INFO[currentDialogAction].cancelButtonText)}
            button2={t(DIALOGS_INFO[currentDialogAction].confirmButtonText)}
            button1Props={{
              color: "greyThree",
              type: "hollow",
              containerStyle: {
                color: colors.textDefault,
                borderColor: colors.borderDefault,
              },
            }}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const userId = userInfo.id;

  return {
    userId,
    isData: true,
    isLoading: false,
    chatBox: state.navigation.navigationObject.chatBox,
    userType: _.get(state, "login.userInfo.user_type", ""),
  };
};

const mapActionCreators = {
  createMessage,
  deleteMessage,
  updateMessage,
  updateChatInfo,
  updateNavigationObject,
  updateTextboxFocus,
  markConversationAsRead,
  setToastMsg,
};

const ChatContainer = React.memo(
  compose(
    I18nHOC({ resource: ["common", "toastMsgs"] }),
    connect(mapStateToProps, mapActionCreators),
    graphql(getConversationsQuery, {
      name: "getConversations",
      skip: ({ conversationId }) => !conversationId,
      options: ({
        conversationId,
        showAllMessages,
        showInstructionMessage,
        excludedItemTypesForConversation,
        includeDeletedMessage,
      }) => ({
        fetchPolicy: "cache-and-network",
        variables: getChatQueryVariables({
          id: conversationId,
          showAllMessages,
          showInstructionMessage,
          extraFilters: {
            excludedItemTypesV2: excludedItemTypesForConversation,
            includeDeletedMessage,
          },
        }),
      }),
      props({
        getConversations,
        ownProps: {
          conversationId,
          isData,
          isLoading,
          showAllMessages,
          showInstructionMessage,
          parentId,
          parentType,
          excludedItemTypesForConversation,
          includeDeletedMessage,
          excludedMessageTypes,
          shouldRefetchConversationOnEvent = () => false,
        },
      }) {
        const queryData = getConversationsFromCache(
          getChatQueryVariables({
            id: conversationId,
            showAllMessages,
            showInstructionMessage,
            extraFilters: {
              excludedItemTypesV2: excludedItemTypesForConversation,
              includeDeletedMessage,
            },
          })
        );

        const conversations = [
          ..._.get(queryData, "messages.edges", []),
        ].reverse();

        const filteredConversations = _.filter(
          conversations,
          ({ node }) => !_.includes(excludedMessageTypes, _.get(node, "type"))
        );

        const totalCounts = _.get(queryData, "messages.edges", 0);

        const totalMessageCount = _.get(queryData, "messages.totalCount", 0);
        const unreadCount = _.get(queryData, "messages.unreadCount", 0);

        const pageInfo = _.get(queryData, "messages.pageInfo", {});

        const pinMessages = _.get(queryData, "pinMessages", {});
        const pinMessagesPageInfo = _.get(
          queryData,
          "pinMessages.pageInfo",
          {}
        );

        return {
          isData: !_.isEmpty(queryData) && isData,
          conversations: filteredConversations,
          pageInfo,
          getConversations,
          networkStatus: getConversations["networkStatus"],
          totalCounts,
          totalMessageCount,
          unreadCount,
          pinMessages,
          pinMessagesPageInfo,
          onPusherEventTriggered: params => {
            if (
              (parentId == params.parentId &&
                parentType == params.parentType) ||
              (params?.event === "assignmentActivityReceived" &&
                parentType === "STUDENT_ASSIGNMENT") ||
              _.isFunction(shouldRefetchConversationOnEvent)
                ? shouldRefetchConversationOnEvent(params)
                : false
            ) {
              getConversations.refetch();
            }
          },
          isLoading:
            getConversations["networkStatus"] == 1 ||
            getConversations["networkStatus"] == 2 ||
            isLoading,
          fetchMoreFeed: () => {
            const endCursor = _.get(pageInfo, "endCursor", null);

            return getConversations.fetchMore({
              variables: getChatQueryVariables({
                id: conversationId,
                showAllMessages,
                showInstructionMessage,
                endCursor,
                extraFilters: {
                  excludedItemTypesV2: excludedItemTypesForConversation,
                  includeDeletedMessage,
                },
              }),

              updateQuery: (previousResult, { fetchMoreResult }) => {
                const previousField = previousResult.node;
                const newField = fetchMoreResult.node;

                return {
                  ...previousResult,
                  node: {
                    ...previousField,
                    messages: {
                      ...previousField.messages,
                      pageInfo: newField.messages.pageInfo,
                      totalCount: newField.messages.totalCount,
                      edges: [
                        ...previousField.messages.edges,
                        ...newField.messages.edges,
                      ],
                    },
                  },
                };
              },
            });
          },
        };
      },
    }),
    withPusherBind([
      "chatMessageReceived",
      "chatMessageReadReceipt",
      "assignmentActivityReceived",
      "assignmentFeedUpdate",
    ]),
    withLoader
  )(ChatContainerLocal)
);

ChatContainer.defaultProps = {
  isV2: false,
  deleteOtherMessage: false,
  showFlatMessage: false,
  showMessageCount: false,
  showAllMessages: false,
  showSenderInfoAlways: false,
  showInstructionMessage: false,
  allowSendMessage: true,
  excludeItemTypesV2: [],
  excludedItemTypesForConversation: [],
  attachmentCardType: "verticalHorizontal",
  mode: "edit",
  isClassDiscussionEnabled: true,
  showNoDataComp: false,
  showRemarkOption: false,
  addCommentFooter: false,
  collaborators: [],
  otherUserId: "",
  taggableTextAreaProps: {},
  emptyViewStyle: {},
  emptyHeaderText: "classRoom:no_conversations_yet",
  emptyText: "common:chat_empty_text",
  emptyImageUrl: `${getCloudUrl()}/assets/illustrations/chat_empty_icon.png`,
  showEmptyViewDescription: true,
  includeDeletedMessage: false,
  shouldMarkConversationAsRead: true,
  footerTextAreaStyle: {},
  messageSubConStyle: {},
  attachmentListContainerStyle: {},
  flatMsgContainerStyle: {},
  emptyStateProps: {},
  hideZeroPrivateMessageCount: false,
  labelKeySingular: "common:private_messages",
  labelKeyPlural: "common:private_msg_with_label",
  messageContextLabel: "",
  showCommentContextBox: false,
  messageContextBoxStyle: {},
  horizontalMessageCardV2: false,
  dropDownConStyle: {},
  attachmentListContainerStyleV2: {},
  scrollLatestMessage: true,
  isEditAndDeleteEnabled: true,
  showLightWeightAvatar: false,
  saveDraftMessage: false,
  excludedMessageTypes: [],
  dsVersion: "1.0",
  chatPanelRef: null,
  enableChatPanel: false,
  isMediaRestricted: false,
};

ChatContainer.propTypes = {
  isV2: PropTypes.bool,
  deleteOtherMessage: PropTypes.bool,
  showFlatMessage: PropTypes.bool,
  showMessageCount: PropTypes.bool,
  showAllMessages: PropTypes.bool,
  showSenderInfoAlways: PropTypes.bool,
  showInstructionMessage: PropTypes.bool,
  allowSendMessage: PropTypes.bool,
  excludeItemTypesV2: PropTypes.any,
  excludedItemTypesForConversation: PropTypes.any,
  attachmentCardType: PropTypes.string,
  mode: PropTypes.string,
  isClassDiscussionEnabled: PropTypes.bool,
  showNoDataComp: PropTypes.bool,
  showRemarkOption: PropTypes.bool,
  addCommentFooter: PropTypes.bool,
  collaborators: PropTypes.array,
  otherUserId: PropTypes.string,
  taggableTextAreaProps: PropTypes.object,
  emptyViewStyle: PropTypes.object,
  emptyHeaderText: PropTypes.string,
  emptyText: PropTypes.string,
  emptyImageUrl: PropTypes.string,
  showEmptyViewDescription: PropTypes.bool,
  includeDeletedMessage: PropTypes.bool,
  shouldMarkConversationAsRead: PropTypes.bool,
  messageSubConStyle: PropTypes.object,
  attachmentListContainerStyle: PropTypes.object,
  flatMsgContainerStyle: PropTypes.object,
  emptyStateProps: PropTypes.object,
  hideZeroPrivateMessageCount: PropTypes.bool,
  labelKeySingular: PropTypes.string,
  labelKeyPlural: PropTypes.string,
  messageContextLabel: PropTypes.string,
  showCommentContextBox: PropTypes.bool,
  messageContextBoxStyle: PropTypes.object,
  horizontalMessageCardV2: PropTypes.bool,
  dropDownConStyle: PropTypes.object,
  attachmentListContainerStyleV2: PropTypes.object,
  scrollLatestMessage: PropTypes.bool,
  isEditAndDeleteEnabled: PropTypes.bool,
  showLightWeightAvatar: PropTypes.bool,
  saveDraftMessage: PropTypes.bool,
  excludedMessageTypes: PropTypes.array,
  dsVersion: PropTypes.string,
  enableChatPanel: PropTypes.bool,
  chatPanelRef: PropTypes.object,
  isMediaRestricted: PropTypes.bool,
};

export default ChatContainer;
